import { useEffect } from 'react';
import { faBadgeCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { Chip, Tooltip, Typography } from '@mui/material';
import { ColumnHeader, SimpleTable } from './components/SimpleTable';
import {
  FiltersBar,
  SelectFreeOffers,
  SelectConvertedTo,
  SelectConversionStatus,
} from './CrmFiltersBar';
import TOOLTIPS from './tooltips';
import { trackEvent } from '@services/tracking';

export type FreeUserDto = {
  id: number;
  name: string;
  email: string;
  isEmailVerified: boolean;
  instagramProfile: string;
  marketingConsent: boolean;
  freeOtpOffers: Array<{ id: number; name: string }>;
  claimedAt: string;
  convertedToProduct: { name: string; productType: string } | null;
  convertedAt: string | null;
  allPurchasedProducts: Array<{ id: number; name: string }>;
};

const ChipList = ({
  items,
}: {
  items: Array<{ id: number; name: string }>;
}) => (
  <div className="flex flex-wrap gap-2">
    {items.map((item) => (
      <Tooltip key={item.id} title={item.name}>
        <Chip
          label={item.name}
          className="border border-solid border-[#ECEDF2] bg-white text-[#151E34] h-[26px] text-sm max-w-[200px]"
        />
      </Tooltip>
    ))}
  </div>
);

const columns = [
  {
    title: 'Name',
    field: 'name',
    width: '8%',
    cellStyle: { width: '8%' },
  },
  {
    title: <ColumnHeader label="Email" title={TOOLTIPS.email} />,
    width: '15%',
    cellStyle: { width: '15%' },
    render: (row: FreeUserDto) => (
      <div className="flex items-center">
        {row.isEmailVerified && row.marketingConsent ? (
          <Icon icon={faBadgeCheck} className="text-primary mr-2" />
        ) : null}
        <span className="select-all">{row.email}</span>
      </div>
    ),
  },
  {
    title: 'Instagram',
    width: '9%',
    cellStyle: { width: '9%' },
    render: (row: FreeUserDto) => (
      <div>
        {row.marketingConsent
          ? row.instagramProfile
          : row.instagramProfile?.replace(/./g, '*')}
      </div>
    ),
  },
  {
    title: <ColumnHeader label="Marketing" title={TOOLTIPS.marketing} />,
    width: '7%',
    cellStyle: { width: '7%' },
    render: (row: FreeUserDto) => (
      <div>{row.marketingConsent ? 'Yes' : 'No'}</div>
    ),
  },
  {
    title: 'Free offers redeemed',
    width: '17%',
    cellStyle: { width: '17%' },
    render: (row: FreeUserDto) => <ChipList items={row.freeOtpOffers} />,
  },
  {
    title: 'Redemption date',
    width: '8%',
    cellStyle: { width: '8%' },
    render: (row: FreeUserDto) => (
      <div>
        {row.claimedAt ? new Date(row.claimedAt).toLocaleDateString() : '-'}
      </div>
    ),
  },
  {
    title: '1st paid offer',
    width: '11%',
    cellStyle: { width: '11%' },
    render: (row: FreeUserDto) => (
      <Tooltip title={row.convertedToProduct?.name ?? ''}>
        <div className="truncate max-w-[200px]">
          {row.convertedToProduct?.name ?? '-'}
        </div>
      </Tooltip>
    ),
  },
  {
    title: 'Converted at',
    width: '8%',
    cellStyle: { width: '8%' },
    render: (row: FreeUserDto) => (
      <div>
        {row.convertedAt ? new Date(row.convertedAt).toLocaleDateString() : '-'}
      </div>
    ),
  },
  {
    title: 'All products purchased',
    width: '17%',
    cellStyle: { width: '17%' },
    render: (row: FreeUserDto) => <ChipList items={row.allPurchasedProducts} />,
  },
].map((c) => ({ ...c, sorting: false }));

export type FreeOffersTableProps = {
  data: FreeUserDto[];
  totalResults: number;
};
export function FreeOffersTable({
  data,
  totalResults,
  ...props
}: FreeOffersTableProps) {
  useEffect(() => {
    trackEvent({
      type: 'Viewed Free Offers Table',
      payload: {},
    });
  }, []);

  return (
    <div className="max-w-full">
      <FiltersBar totalResults={totalResults}>
        <SelectFreeOffers />
        <SelectConvertedTo />
        <SelectConversionStatus />
      </FiltersBar>
      <div className="my-3 text-sm text-[#8A8E99]">
        <Typography>
          Contact information is shown for all users. The Marketing column
          indicates whether you have permission to email a user for promotional
          or non-support purposes.
        </Typography>
      </div>
      <SimpleTable {...props} columns={columns} data={data} />
    </div>
  );
}
